import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useParams } from "react-router-dom";
import { useGetChainQuery, useUpdateChainMutation } from "../query/chainsQuery";
import { LoadingContainer } from "../components/LoadingContainer";
import { formatDate } from "../utils/dateUtils";
import { useGetResellerQuery, useGetResellersQuery } from "../query/resellersQuery";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { SelectInput } from "../components/SelectInput";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { FirestoreConsoleButton } from "../components/FirestoreConsoleButton";
import { GcpLogsConsoleButton } from "../components/GcpLogsConsoleButton";

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.x3}px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${spacing.x2}px;
  }
`;

const InfoCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const InfoLabel = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: ${fontSizes.large}px;
  font-weight: 500;
  color: ${colors.text};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  &:not(:has(a)) {
    display: none;
  }
`;

export const ChainOverview = () => {
  const { t } = useTranslation();
  const { chainId } = useParams<{ chainId: string }>();
  const {data: chain, isLoading} = useGetChainQuery(chainId);
  const {data: reseller} = useGetResellerQuery(chain?.resellerId || undefined);
  const {data: resellers} = useGetResellersQuery();
  const isAdmin = useIsAdmin();
  const { mutate: updateChain, isPending: isUpdatePending } = useUpdateChainMutation();

  const handleResellerChange = (e: { target: { value: string } }) => {
    const newResellerId = e.target.value === "" ? null : (e.target.value);
    if (chainId && chain) {
      updateChain({
        chainId,
        chain: { resellerId: newResellerId },
      });
    }
  };

  if (isLoading) {
    return <LoadingContainer message={t("chainOverview.loading")} />;
  }
  if (!chain) {
    return <LoadingContainer message={t("chainOverview.notFound")} />;
  }
  return (
    <Container>
      <PageHeader>
        <h1 style={{ margin: 0 }}>{t("chainOverview.pageTitle")}</h1>
        <PageHeaderActions>
          <FirestoreConsoleButton path={`chains/${chainId ?? ""}`} />
          <GcpLogsConsoleButton searchId={chainId ?? ""} />
        </PageHeaderActions>
      </PageHeader>
      <DashboardGrid>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("chainOverview.chainName")}</InfoLabel>
            <InfoValue>{chain.name}</InfoValue>
          </InfoRow>
          {isAdmin && (
            <InfoRow>
              <InfoLabel>{t("chainOverview.id")}</InfoLabel>
              <InfoValue>{chain.id}</InfoValue>
            </InfoRow>
          )}
          {chain.brand && (
            <InfoRow>
              <InfoLabel>{t("chainOverview.brand")}</InfoLabel>
              <InfoValue>{chain.brand}</InfoValue>
            </InfoRow>
          )}
        </InfoCard>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("chainOverview.address")}</InfoLabel>
            <InfoValue>
              {chain.street}
              <br />
              {chain.postalcode} {chain.city}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("chainOverview.country")}</InfoLabel>
            <InfoValue>{chain.country}</InfoValue>
          </InfoRow>
        </InfoCard>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("chainOverview.organizationNumber")}</InfoLabel>
            <InfoValue>{chain.orgNumber}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("chainOverview.reseller")}</InfoLabel>
            {isAdmin ? (
              <SelectWrapper>
                <SelectInput
                  value={chain.resellerId || ""}
                  onChange={handleResellerChange}
                  disabled={isUpdatePending}
                >
                  <option value="">{t("chainOverview.noReseller")}</option>
                  {resellers?.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </SelectInput>
                {isUpdatePending && (
                  <LoadingSpinner size={16} borderWidth={2} />
                )}
              </SelectWrapper>
            ) : (
              <InfoValue>{reseller?.name || chain.resellerId || t("chainOverview.noReseller")}</InfoValue>
            )}
          </InfoRow>
        </InfoCard>
        {(chain.createdAt || chain.updatedAt) && (
          <InfoCard>
            {chain.createdAt && (
              <InfoRow>
                <InfoLabel>{t("chainOverview.createdAt")}</InfoLabel>
                <InfoValue>{formatDate(chain.createdAt)}</InfoValue>
              </InfoRow>
            )}
            {chain.updatedAt && (
              <InfoRow>
                <InfoLabel>{t("chainOverview.updatedAt")}</InfoLabel>
                <InfoValue>{formatDate(chain.updatedAt)}</InfoValue>
              </InfoRow>
            )}
          </InfoCard>
        )}
      </DashboardGrid>
    </Container>
  );
};
